
/**
 * @fileoverview Мапперы для заказов между DB и Frontend.
 */
import { DbRow, DbInsert, DbUpdate } from '../../types/db';
import { Order, OrderItem, OrderWithDetails, DbOrder } from '../../types/entities/order';

type DbOrderWithClient = DbOrder & {
  clients?: {
    id: string;
    full_name: string | null;
    company_name: string | null;
  } | null;
};

// Minimal helper types used for narrowing legacy/related structures
type LegacyItem = {
  id?: string | null;
  name?: string | null;
  quantity?: number | null;
  unit_price?: number | null;
  price?: number | null;
  inventory_item_id?: string | null;
  inventory_id?: string | null;
  product_id?: string | null;
  store_product_id?: string | null;
};

type RelatedItem = {
  id?: string;
  item_type?: 'inventory' | 'store_product' | 'service';
  inventory_item_id?: string | null;
  inventory_id?: string | null;
  store_product_id?: string | null;
  service_id?: string | null;
  quantity?: number | null;
  unit_price?: number | null;
  price?: number | null;
  total_price?: number | null;
  inventory?: { id?: string | null; name?: string | null } | null;
  store_products?: { id?: string | null; name?: string | null } | null;
  services?: { id?: string | null; name?: string | null } | null;
};

// Utility functions
function parseDate(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
}

function parseJson<T>(json: unknown): T[] {
  if (json == null) return [];
  if (typeof json === 'string') {
    try {
      const parsed = JSON.parse(json);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Failed to parse JSON string:', error);
      return [];
    }
  }
  return Array.isArray(json) ? (json as T[]) : [];
}

// Функция для определения isStorePurchase на основе бизнес-логики (fallback)
// Используется только если поле is_store_purchase отсутствует в БД
function determineIsStorePurchaseFallback(dbOrder: DbOrder): boolean {
  // Парсим элементы заказа
  const inventoryItems = parseJson<LegacyItem>(dbOrder.inventory); // Обновлено: inventory вместо inventory_items после миграции
  const storeItems = parseJson<LegacyItem>(dbOrder.store_items);
  
  // Если есть элементы инвентаря (аренда), то это не покупка
  if (inventoryItems && inventoryItems.length > 0) {
    return false;
  }
  
  // Если есть только товары магазина и нет инвентаря, то это покупка
  if (storeItems && storeItems.length > 0 && inventoryItems.length === 0) {
    return true;
  }
  
  // По умолчанию считаем арендой (более безопасный вариант для статистики)
  return false;
}

// DB to Frontend mappers
export function mapDbOrderToOrder(dbOrder: DbOrder | DbOrderWithClient): Order {
  // Используем значение из БД (поле теперь существует и должно быть заполнено)
  const isStorePurchase = dbOrder.is_store_purchase ?? false;

  // Базовые legacy массивы
  let inventoryItems = parseJson<LegacyItem>(dbOrder.inventory);
  let storeItems = parseJson<LegacyItem>(dbOrder.store_items);
  let services: LegacyItem[] = [];

  // Если пришли связки order_items с вложенными именами — дополним legacy массивы для отображения
  const relatedItems = (dbOrder as unknown as { order_items?: RelatedItem[] | null }).order_items;
  const hasNames = (arr: unknown[]) => Array.isArray(arr) && arr.some((it) => (it as { name?: string | null }).name);
  if (Array.isArray(relatedItems) && relatedItems.length > 0) {
    const invFromJoin = relatedItems
      .filter(it => it.item_type === 'inventory' && it?.inventory?.name)
      .map(it => ({ id: it.inventory?.id ?? it.inventory_item_id ?? it.inventory_id ?? null, name: it.inventory?.name }));
    const storeFromJoin = relatedItems
      .filter(it => it.item_type === 'store_product' && it?.store_products?.name)
      .map(it => ({ id: it.store_products?.id ?? it.store_product_id ?? null, name: it.store_products?.name }));
    const servFromJoin = relatedItems
      .filter(it => it.item_type === 'service' && it?.services?.name)
      .map(it => ({ id: it.services?.id ?? it.service_id ?? null, name: it.services?.name }));

    // Подставляем только если в legacy массивах нет читабельных имен
    if (!hasNames(inventoryItems) && invFromJoin.length) inventoryItems = invFromJoin;
    if (!hasNames(storeItems) && storeFromJoin.length) storeItems = storeFromJoin;
    if (!hasNames(services) && servFromJoin.length) services = servFromJoin;
  }

  // Вспомогательная функция: преобразует LegacyItem[] в OrderItem[]
  const legacyToOrderItems = (items: LegacyItem[], type: 'inventory' | 'store_product' | 'service'): OrderItem[] => {
    return items.map((it, index) => ({
      id: `${dbOrder.id}_legacy_${type}_${index}`,
      orderId: dbOrder.id,
      itemType: type,
      inventoryItemId: type === 'inventory' ? (it.id || it.inventory_item_id || it.inventory_id || null) : null,
      storeProductId: type === 'store_product' ? (it.id || it.product_id || it.store_product_id || null) : null,
      serviceId: type === 'service' ? (it.id || null) : null,
      quantity: it.quantity ?? 1,
      unitPrice: it.unit_price ?? it.price ?? 0,
      totalPrice: (it.quantity ?? 1) * (it.unit_price ?? it.price ?? 0),
      createdAt: new Date(dbOrder.created_at),
      updatedAt: new Date(dbOrder.updated_at),
    }));
  };

  const result: Order = {
    id: dbOrder.id,
    userId: dbOrder.user_id || '',
    createdAt: new Date(dbOrder.created_at || new Date().toISOString()),
    updatedAt: new Date(dbOrder.updated_at || new Date().toISOString()),
    clientId: dbOrder.client_id,
    rentalPointId: dbOrder.rental_point_id,
    startDate: parseDate(dbOrder.start_date),
    endDate: parseDate(dbOrder.end_date),
    status: dbOrder.status,
    paymentStatus: dbOrder.payment_status,
    totalAmount: dbOrder.total_amount,
    discount: dbOrder.discount_amount, // FIXED: используем discount_amount из реальной схемы БД
    depositAmount: dbOrder.deposit_amount,
    notes: dbOrder.notes,
    returnPointId: dbOrder.return_point_id,
    paymentMethodId: dbOrder.payment_method_id,
    paymentMethod: dbOrder.payment_method,
    tariffId: dbOrder.tariff_id,
    // FIXED: Добавлены недостающие поля из схемы Order
    orderNumber: dbOrder.order_number || null,
    returnDate: parseDate(dbOrder.return_date),
    deliveryType: dbOrder.delivery_type || null,
    deliveryAddress: dbOrder.delivery_address || null,
    // Поля, которых нет в текущей схеме Supabase - маппим из legacy JSON/обогащаем из join при наличии
    inventoryItems: legacyToOrderItems(inventoryItems, 'inventory'),
    storeItems: legacyToOrderItems(storeItems, 'store_product'),
    // Используем вычисленное значение или реальное из БД (когда поле будет добавлено)
    isStorePurchase,
    signatureId: dbOrder.signature_id || null,
    // Поля из схемы Supabase
    depositStatus: dbOrder.deposit_status,
    contractGenerated: dbOrder.contract_generated,
    promoCode: dbOrder.promo_code,
    // Completion fields
    returnConditionId: dbOrder.return_condition_id,
    clientLoyaltyStatus: dbOrder.client_loyalty_status,
    // Archive fields
    archivedAt: dbOrder.archived_at ? new Date(dbOrder.archived_at) : null,
    archivedBy: dbOrder.archived_by ?? null,
  };

  // Негласное поле services используется в UI: добавляем его безопасно через any
  (result as unknown as { services?: LegacyItem[] }).services = services;
  return result;
}

export function mapDbOrdersToOrders(dbOrders: DbOrder[]): Order[] {
  return dbOrders.map(mapDbOrderToOrder);
}

// Вспомогательная функция для маппинга товаров заказа из relation order_items (приоритет) или JSON-полей (fallback)
function mapDbOrderItemsToOrderItems(dbOrder: DbOrder): OrderItem[] {
  const orderItems: OrderItem[] = [];

  // 1) Приоритет: нормализованные строки order_items, если они были выбраны в selectQuery
  const relatedItems = (dbOrder as unknown as { order_items?: RelatedItem[] | null }).order_items;
  if (Array.isArray(relatedItems) && relatedItems.length > 0) {
    relatedItems.forEach((item: RelatedItem, index: number) => {
      // Предпочитаем явный тип из item.item_type, иначе определяем по наличию ссылок
      let itemType: 'inventory' | 'store_product' | 'service' =
        (item.item_type as 'inventory' | 'store_product' | 'service') || 'inventory';
      if (!item.item_type) {
        if (item.store_product_id) itemType = 'store_product';
        if (item.service_id) itemType = 'service';
      }

      // Извлекаем читаемые имена из связанных сущностей, если они были загружены в selectQuery
      const inventoryName = item.inventory?.name ?? null;
      const storeProductName = item.store_products?.name ?? null;
      const serviceName = item.services?.name ?? null;

      // Добавляем дополнительные поля с именами. Тип OrderItem их не требует,
      // но наличие этих ключей не нарушает типизацию при фактическом исполнении.
      orderItems.push({
        id: item.id ?? `${dbOrder.id}_rel_${index}`,
        orderId: dbOrder.id,
        itemType,
        inventoryItemId: (item.inventory_item_id ?? item.inventory_id) ?? null,
        storeProductId: item.store_product_id ?? null,
        serviceId: item.service_id ?? null,
        quantity: item.quantity ?? 1,
        unitPrice: item.unit_price ?? item.price ?? 0,
        totalPrice: item.total_price ?? ((item.quantity ?? 1) * (item.unit_price ?? item.price ?? 0)),
        createdAt: new Date(dbOrder.created_at),
        updatedAt: new Date(dbOrder.updated_at),
        // Дополнительные пользовательские поля (необязательные)
        ...(inventoryName ? { inventoryName } : {}),
        ...(storeProductName ? { storeProductName } : {}),
        ...(serviceName ? { serviceName } : {}),
      } as unknown as OrderItem);
    });
    return orderItems;
  }

  // 2) Fallback: legacy JSON-поля на заказе (inventory, store_items)
  if (dbOrder.inventory && Array.isArray(dbOrder.inventory)) {
    (dbOrder.inventory as unknown as LegacyItem[]).forEach((item: LegacyItem, index: number) => {
      orderItems.push({
        id: `${dbOrder.id}_inventory_${index}`,
        orderId: dbOrder.id,
        itemType: 'inventory',
        inventoryItemId: item.id || item.inventory_item_id || item.inventory_id || null,
        storeProductId: null,
        serviceId: null,
        quantity: item.quantity || 1,
        unitPrice: item.unit_price || item.price || 0,
        totalPrice: (item.quantity || 1) * (item.unit_price || item.price || 0),
        createdAt: new Date(dbOrder.created_at),
        updatedAt: new Date(dbOrder.updated_at)
      });
    });
  }

  if (dbOrder.store_items && Array.isArray(dbOrder.store_items)) {
    (dbOrder.store_items as unknown as LegacyItem[]).forEach((item: LegacyItem, index: number) => {
      orderItems.push({
        id: `${dbOrder.id}_store_${index}`,
        orderId: dbOrder.id,
        itemType: 'store_product',
        inventoryItemId: null,
        storeProductId: item.id || item.product_id || item.store_product_id || null,
        serviceId: null,
        quantity: item.quantity || 1,
        unitPrice: item.unit_price || item.price || 0,
        totalPrice: (item.quantity || 1) * (item.unit_price || item.price || 0),
        createdAt: new Date(dbOrder.created_at),
        updatedAt: new Date(dbOrder.updated_at)
      });
    });
  }

  return orderItems;
}

// Маппер для заказов с деталями клиента
export function mapDbOrderToOrderWithDetails(dbOrder: DbOrderWithClient): OrderWithDetails {
  const baseOrder = mapDbOrderToOrder(dbOrder);
  
  // Извлекаем имя клиента из связанных данных
  const clientName = dbOrder.clients?.full_name || dbOrder.clients?.company_name || null;
  
  return {
    ...baseOrder,
    clientId: dbOrder.client_id || '', // Гарантируем непустой clientId для форм
    clientName,
    orderItems: mapDbOrderItemsToOrderItems(dbOrder)
  };
}

// Frontend to DB mappers
export function mapOrderToDbInsert(order: Partial<Order>): DbInsert<'orders'> {
  const payload: Record<string, unknown> = {
    user_id: order.userId,
    client_id: order.clientId,
    rental_point_id: order.rentalPointId!,
    return_point_id: order.returnPointId,
    tariff_id: order.tariffId,
    start_date: order.startDate?.toISOString(),
    end_date: order.endDate?.toISOString(),
    status: order.status,
    payment_method_id: order.paymentMethodId,
    payment_status: order.paymentStatus,
    discount_amount: order.discount, // FIXED: используем discount_amount для БД
    deposit_amount: order.depositAmount,
    total_amount: order.totalAmount,
    notes: order.notes,
    // FIXED: Добавлены новые поля из схемы Order
    order_number: order.orderNumber,
    return_date: order.returnDate?.toISOString(),
    delivery_type: order.deliveryType,
    delivery_address: order.deliveryAddress,
    // Поля из схемы Supabase
    deposit_status: order.depositStatus,
    contract_generated: order.contractGenerated,
    // АРХИТЕКТУРНОЕ РЕШЕНИЕ: НЕ мапим location_id, используем только rental_point_id
    promo_code: order.promoCode,
  };
  if (order.isStorePurchase !== undefined) payload.is_store_purchase = order.isStorePurchase;
  if (order.returnConditionId !== undefined) payload.return_condition_id = order.returnConditionId;
  if (order.clientLoyaltyStatus !== undefined) payload.client_loyalty_status = order.clientLoyaltyStatus;
  return payload as unknown as DbInsert<'orders'>;
}

export function mapOrderUpdateToDbUpdate(orderUpdate: Partial<Order>): DbUpdate<'orders'> {
  const dbUpdate: Record<string, unknown> = {};
  
  // Проверяем, что orderUpdate существует
  if (!orderUpdate) {
    return dbUpdate as unknown as DbUpdate<'orders'>;
  }
  
  if (orderUpdate.clientId !== undefined) dbUpdate.client_id = orderUpdate.clientId;
  if (orderUpdate.rentalPointId !== undefined) dbUpdate.rental_point_id = orderUpdate.rentalPointId;
  if (orderUpdate.returnPointId !== undefined) dbUpdate.return_point_id = orderUpdate.returnPointId;
  if (orderUpdate.tariffId !== undefined) dbUpdate.tariff_id = orderUpdate.tariffId;
  if (orderUpdate.startDate !== undefined) dbUpdate.start_date = orderUpdate.startDate?.toISOString();
  if (orderUpdate.endDate !== undefined) dbUpdate.end_date = orderUpdate.endDate?.toISOString();
  if (orderUpdate.status !== undefined) dbUpdate.status = orderUpdate.status;
  if (orderUpdate.paymentMethodId !== undefined) dbUpdate.payment_method_id = orderUpdate.paymentMethodId;
  if (orderUpdate.paymentStatus !== undefined) dbUpdate.payment_status = orderUpdate.paymentStatus;
  if (orderUpdate.discount !== undefined) dbUpdate.discount_amount = orderUpdate.discount; // FIXED: используем discount_amount для БД
  if (orderUpdate.depositAmount !== undefined) dbUpdate.deposit_amount = orderUpdate.depositAmount;
  if (orderUpdate.totalAmount !== undefined) dbUpdate.total_amount = orderUpdate.totalAmount;
  if (orderUpdate.notes !== undefined) dbUpdate.notes = orderUpdate.notes;
  // FIXED: Добавлены новые поля из схемы Order
  if (orderUpdate.orderNumber !== undefined) dbUpdate.order_number = orderUpdate.orderNumber;
  if (orderUpdate.returnDate !== undefined) dbUpdate.return_date = orderUpdate.returnDate?.toISOString();
  if (orderUpdate.deliveryType !== undefined) dbUpdate.delivery_type = orderUpdate.deliveryType;
  if (orderUpdate.deliveryAddress !== undefined) dbUpdate.delivery_address = orderUpdate.deliveryAddress;
  // Поля из схемы Supabase
  if (orderUpdate.depositStatus !== undefined) dbUpdate.deposit_status = orderUpdate.depositStatus;
  if (orderUpdate.contractGenerated !== undefined) dbUpdate.contract_generated = orderUpdate.contractGenerated;
  // АРХИТЕКТУРНОЕ РЕШЕНИЕ: НЕ мапим location_id, используем только rental_point_id
  if (orderUpdate.promoCode !== undefined) dbUpdate.promo_code = orderUpdate.promoCode;
  if (orderUpdate.isStorePurchase !== undefined) dbUpdate.is_store_purchase = orderUpdate.isStorePurchase;
  // Completion fields
  if (orderUpdate.returnConditionId !== undefined) dbUpdate.return_condition_id = orderUpdate.returnConditionId;
  if (orderUpdate.clientLoyaltyStatus !== undefined) dbUpdate.client_loyalty_status = orderUpdate.clientLoyaltyStatus;

  return dbUpdate as unknown as DbUpdate<'orders'>;
}

